import {
  GeneralPermissions,
  MarketingPermissions,
  OrderManagementPermissions,
} from "@redotech/account-common/permissions";
import { isRedoEmailAddress } from "@redotech/account-common/redo-domains";
import { MarketingFlagKey } from "@redotech/feature-flags-keys-marketing/keys";
import { OnboardingFlagKey } from "@redotech/feature-flags-keys-onboarding/keys";
import { Redirect } from "@redotech/react-router-util/redirect";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { reviewsRoutes } from "@redotech/redo-merchant-review-app/route";
import type { ShopperAgentConversationsPageProps } from "@redotech/redo-merchant-shopper-agent-app/conversations/shopper-agent-conversations-page";
import type { ShopperAgentAuthor } from "@redotech/redo-shopper-agent-common/conversation-author";
import { ComponentType, lazy, memo, Suspense, useContext } from "react";
import { matchPath, Navigate, Outlet } from "react-router-dom";
import {
  MARKETING_ANALYTICS_TABS_URL_PARAM,
  MarketingAnalyticsPage,
} from "../../../analytics/marketing/marketing-analytics";
import {
  automationRoutes,
  backwardsCompatibleEmailSmsRoutes,
  emailSmsRoutes,
  loyaltyRoutes,
} from "../../../marketing/route";
import { spotlightRoutes } from "../../../spotlights/route";

const HIDE_PADDING_ROUTES: string[] = [
  "/email-sms/audience/customers/:customerId",
  "/automations/:automationId",
  "/email-sms/campaigns/:campaignId/edit/*",
  "/email-sms/campaigns/:campaignId/edit-with-ai/template",
  "/email-sms/campaigns/:campaignId/details",
  "/email-sms/segments/:segmentId",
  "/email-sms/templates/email/:id",
  "/email-sms/templates/sms/:id",
  "/email-sms/templates/click-map/:templateId",
  "/email-sms/signups/builder/:id",
  "/spotlights/new",
  "/spotlights/builder/:id",
  "/email-sms/spotlights/new",
  "/email-sms/spotlights/builder/:id",
  "/tracking/tracking-page",
  "/tracking/tracking-page/flow",
  "/reviews/reviews-displays-widgets/review-form",
  "/reviews/reviews-displays-widgets/star-rating-widget",
  "/loyalty/page/customize",
  "/tracking/tracking-page/login-form-builder",
  "/tracking/tracking-page/tracking-page-builder",
  "/email-sms/deliverability/*",
  "/recover/builder/:recoverConfigId",
  "/recover/conversation-log",
  "/marketing-agent/playbook",
  "/marketing-agent",
  "/text-concierge/setup",
  "/text-concierge/conversations",
  "/text-concierge/settings",
  "/text-concierge/analytics",
  "/contextual",
  "/contextual/ai",
  "/contextual/ai/v3",
  "/analytics/reports",
  "/analytics/reports/*",
];

function hidePadding(url: string, search: { search: string }): boolean {
  if (
    url.includes("/marketing/analytics") &&
    new URLSearchParams(search.search).get(
      MARKETING_ANALYTICS_TABS_URL_PARAM,
    ) === MarketingAnalyticsPage.BACK_IN_STOCK
  ) {
    return true;
  }

  return HIDE_PADDING_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/marketing${route}`, url),
  );
}

const FULLSCREEN_ROUTES: string[] = [
  "/upsell-page/:id",
  "/email-sms/signups/builder/:id",
  "/spotlights/new",
  "/spotlights/builder/:id",
  "/email-sms/spotlights/new",
  "/email-sms/spotlights/builder/:id",
  "/email-sms/templates/email/:id",
  "/email-sms/templates/sms/:id",
  "/email-sms/templates/click-map/:templateId",
  "/email-sms/segments/:segmentId",
  "/email-sms/campaigns/:campaignId/edit/*",
  "/email-sms/campaigns/:campaignId/edit-with-ai/template",
  "/automations/:automationId",
  //  "/tracking/tracking-page",
  "/reviews/reviews-displays-widgets/review-form",
  "/reviews/reviews-displays-widgets/star-rating-widget",
  "/loyalty/page/customize",
  "/tracking/tracking-page/login-form-builder",
  "/tracking/tracking-page/tracking-page-builder",
  "/recover/builder/:recoverConfigId",
];

const HIDE_HEADER_ROUTES: string[] = [...FULLSCREEN_ROUTES];
function hideHeader(url: string): boolean {
  return HIDE_HEADER_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/marketing${route}`, url),
  );
}

const HIDE_NAV_ROUTES: string[] = [...FULLSCREEN_ROUTES];

function hideNav(url: string): boolean {
  return HIDE_NAV_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/marketing${route}`, url),
  );
}

const TextConciergeRedirect = memo(function TextConciergeRedirect({
  subPath,
}: {
  subPath?: string;
}) {
  const routes = useRequiredContext(MerchantRoutesContext);
  return (
    <Navigate
      replace
      to={
        subPath
          ? routes.getShopperAgentSubRoute(subPath)
          : routes.shopperAgentHref
      }
    />
  );
});

function hideSearchAndBanner(url: string): boolean {
  return FULLSCREEN_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/marketing${route}`, url),
  );
}

const ContextualFeatureGate = memo(function ContextualFeatureGate() {
  const marketingRpcClient = useRequiredContext(RedoMarketingRpcClientContext);
  const flagLoad = useLoad(
    (signal) =>
      marketingRpcClient.getBooleanFeatureFlag(
        { key: OnboardingFlagKey.CONTEXTUAL_ONBOARDING },
        { signal },
      ),
    [marketingRpcClient],
  );

  if (flagLoad.pending || flagLoad.value === undefined) {
    return null;
  }

  if (!flagLoad.value.enabled) {
    return <Navigate replace to=".." />;
  }

  return <Outlet />;
});

const DiscountUiFeatureGate = memo(function DiscountUiFeatureGate() {
  const team = useRequiredContext(TeamContext);
  const discountUiEnabled =
    team.featureFlags?.[MarketingFlagKey.DISCOUNT_UI] === true;

  if (!discountUiEnabled) {
    return <Navigate replace to=".." />;
  }

  return <Outlet />;
});

const MarketingCloudLandingPage = lazy(() =>
  import("@redotech/redo-merchant-marketing-app/marketing-cloud/marketing-cloud-landing-page").then(
    (m) => ({ default: m.MarketingCloudLandingPage }),
  ),
);

const MarketingDefaultRoute = memo(function MarketingDefaultRoute() {
  const team = useRequiredContext(TeamContext);
  const settings = team.settings;
  const marketingCloudEnabled =
    !!settings.marketing?.enabled ||
    !!settings.orderTracking?.enabled ||
    !!settings.recover?.enabled ||
    !!settings.reviews?.enabled ||
    !!settings.tnpl?.enabled ||
    !!settings.hsa?.enabled;
  const canViewMarketingSection = useHasPermission(
    GeneralPermissions.OTHER,
    ...Object.values(MarketingPermissions),
  );

  if (!marketingCloudEnabled) {
    return <Navigate replace to="welcome" />;
  }

  if (canViewMarketingSection) {
    return (
      <Suspense fallback={null}>
        <MarketingCloudLandingPage />
      </Suspense>
    );
  }

  return <Navigate replace to=".." />;
});

/**
 * The shared commerce agent view replaced the old Recover conversation log, but
 * it still has gaps the old one covered, so Redo staff get a temporary escape
 * hatch to the legacy route.
 */
const RecoverConversationLogRoute = memo(function RecoverConversationLogRoute({
  author,
  Page,
}: {
  author: ShopperAgentAuthor;
  Page: ComponentType<ShopperAgentConversationsPageProps>;
}) {
  const routes = useRequiredContext(MerchantRoutesContext);
  const user = useContext(UserContext);
  const legacyViewHref = isRedoEmailAddress(user?.email)
    ? routes.getRecoverSubRoute("conversation-log-legacy")
    : undefined;

  return (
    <Page
      defaultAuthor={author}
      legacyViewHref={legacyViewHref}
      storageNamespace="redo-recover-conversations"
    />
  );
});

// cspell:ignore Permissioned
export const marketingRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Marketing" },
    async lazy() {
      const { Page } = await import("../../page");
      return {
        element: (
          <Page
            hideHeader={hideHeader}
            hideNavbar={hideNav}
            hidePadding={hidePadding}
            hideSearchBarAndBanner={hideSearchAndBanner}
          />
        ),
      };
    },
    children: [
      { index: true, element: <MarketingDefaultRoute /> },
      {
        path: "welcome",
        handle: { breadcrumb: "Welcome" },
        async lazy() {
          const { MarketingWelcomePage } =
            await import("@redotech/redo-merchant-marketing-app/welcome/marketing-welcome-page");
          return { Component: MarketingWelcomePage };
        },
      },
      {
        path: "analytics",
        handle: { breadcrumb: "Analytics" },
        children: [
          {
            index: true,
            handle: {
              permission: MarketingPermissions.VIEW_MARKETING_ANALYTICS,
            },
            async lazy() {
              const { MarketingAnalytics } =
                await import("@redotech/redo-merchant-app/analytics/marketing/marketing-analytics");
              return { Component: MarketingAnalytics };
            },
          },
          {
            path: "order-tracking",
            handle: {
              breadcrumb: "Order Tracking",
              permission: MarketingPermissions.VIEW_ORDER_TRACKING_ANALYTICS,
            },
            async lazy() {
              const { OrderTrackingAnalytics } =
                await import("@redotech/redo-merchant-app/analytics/order-tracking/order-tracking-analytics");
              return { Component: OrderTrackingAnalytics };
            },
          },
          {
            path: "recover",
            handle: {
              breadcrumb: "Recover",
              permission: MarketingPermissions.VIEW_MARKETING_ANALYTICS,
            },
            async lazy() {
              const { RecoverAnalyticsV2Page } =
                await import("@redotech/redo-merchant-marketing-app/recover/recover-analytics-v2-page");
              return { Component: RecoverAnalyticsV2Page };
            },
          },
          {
            path: "reports",
            handle: {
              breadcrumb: "Custom reports",
              permission: OrderManagementPermissions.VIEW_REPORTS,
            },
            children: [
              {
                index: true,
                handle: { permission: OrderManagementPermissions.VIEW_REPORTS },
                async lazy() {
                  const { MarketingCustomReportsTab } =
                    await import("@redotech/redo-merchant-app/analytics/reports/custom/marketing-custom-reports");
                  return { Component: MarketingCustomReportsTab };
                },
              },
              {
                path: "custom/:customReportId",
                handle: {
                  breadcrumb: "Custom",
                  permission: OrderManagementPermissions.VIEW_REPORTS,
                },
                async lazy() {
                  const { MarketingCustomReportPage } =
                    await import("@redotech/redo-merchant-app/analytics/reports/custom/marketing-custom-reports");
                  return { Component: MarketingCustomReportPage };
                },
              },
            ],
          },
        ],
      },
      {
        path: "marketing-agent",
        handle: { breadcrumb: "Marketing Agent" },
        async lazy() {
          const { MarketingAgentRouteGate } =
            await import("@redotech/redo-merchant-marketing-app/marketing-agent/marketing-agent-route-gate");
          return { Component: MarketingAgentRouteGate };
        },
        children: [
          {
            index: true,
            async lazy() {
              const { MarketingAgentIndexRoute } =
                await import("@redotech/redo-merchant-marketing-app/marketing-agent/marketing-agent-index-route");
              return { Component: MarketingAgentIndexRoute };
            },
          },
          { path: "playbook", element: <Navigate replace to="../dashboard" /> },
          {
            path: "dashboard",
            handle: { breadcrumb: "Split Test" },
            async lazy() {
              const { MarketingAgentDashboardRoute } =
                await import("@redotech/redo-merchant-marketing-app/marketing-agent/marketing-agent-dashboard-route");
              return { Component: MarketingAgentDashboardRoute };
            },
          },
          { path: "analytics", element: <Navigate replace to=".." /> },
          {
            path: "conversations",
            handle: { breadcrumb: "Conversations" },
            async lazy() {
              const { MarketingAgentConversationsPage } =
                await import("@redotech/redo-merchant-marketing-app/marketing-agent/marketing-agent-split/pages/conversations/page");
              return { Component: MarketingAgentConversationsPage };
            },
          },
        ],
      },
      { path: "reviews", children: reviewsRoutes },
      { path: "loyalty", children: loyaltyRoutes },
      {
        path: "recover",
        handle: {
          breadcrumb: "Recover",
          permission: MarketingPermissions.VIEW_MARKETING_ANALYTICS,
        },
        children: [
          {
            index: true,
            lazy: async () => {
              const { RecoverLandingPage } =
                await import("@redotech/redo-merchant-marketing-app/recover/recover-landing-page");
              return { Component: RecoverLandingPage };
            },
          },
          {
            path: "welcome",
            handle: { breadcrumb: "Welcome" },
            async lazy() {
              const { RecoverWelcomePage } =
                await import("@redotech/redo-merchant-marketing-app/recover/recover-welcome-page");
              return { Component: RecoverWelcomePage };
            },
          },
          {
            path: "conversation-log",
            handle: { breadcrumb: "Conversation log" },
            async lazy() {
              const { ShopperAgentConversationsPage } =
                await import("@redotech/redo-merchant-shopper-agent-app/conversations/shopper-agent-conversations-page");
              const { ShopperAgentAuthor } =
                await import("@redotech/redo-shopper-agent-common/conversation-author");
              return {
                Component: () => (
                  <RecoverConversationLogRoute
                    author={ShopperAgentAuthor.Recover}
                    Page={ShopperAgentConversationsPage}
                  />
                ),
              };
            },
          },
          {
            path: "conversation-log-legacy",
            handle: { breadcrumb: "Conversation log" },
            async lazy() {
              const { RecoverConversationLogPage } =
                await import("@redotech/redo-merchant-marketing-app/recover/recover-conversation-log-page");
              return { Component: RecoverConversationLogPage };
            },
          },
          {
            path: "builder/:recoverConfigId",
            async lazy() {
              const { RecoverBuilder } =
                await import("@redotech/redo-merchant-marketing-app/recover-builder/recover-builder");
              return { Component: RecoverBuilder };
            },
          },
        ],
      },
      {
        path: "tracking",
        handle: { breadcrumb: "Order tracking" },
        children: [
          {
            index: true,
            lazy: async () => {
              const { OrderTrackingLandingPage } =
                await import("@redotech/redo-merchant-marketing-app/order-tracking/order-tracking-landing-page");
              return { Component: OrderTrackingLandingPage };
            },
          },
          {
            path: "about",
            async lazy() {
              const { AboutTracking } =
                await import("@redotech/redo-merchant-app/setting/about-tracking/about-tracking");
              return { element: <AboutTracking /> };
            },
          },
          {
            path: "orders",
            handle: { breadcrumb: "Orders" },
            children: [
              {
                index: true,
                lazy: async () => {
                  const { OrdersPageWithActiveView } =
                    await import("@redotech/redo-merchant-app/order-tracking/orders/orders-page");
                  return { Component: OrdersPageWithActiveView };
                },
              },
              {
                path: ":viewName",
                lazy: async () => {
                  const { OrdersPageWithActiveView } =
                    await import("@redotech/redo-merchant-app/order-tracking/orders/orders-page");
                  return { Component: OrdersPageWithActiveView };
                },
              },
            ],
          },
          {
            path: "shipments",
            handle: { breadcrumb: "Shipments" },
            children: [
              {
                index: true,
                lazy: async () => {
                  const { ShipmentsPageWithActiveView } =
                    await import("@redotech/redo-merchant-marketing-app/order-tracking/shipments/shipments-page");
                  return { Component: ShipmentsPageWithActiveView };
                },
              },
              {
                path: ":viewName",
                lazy: async () => {
                  const { ShipmentsPageWithActiveView } =
                    await import("@redotech/redo-merchant-marketing-app/order-tracking/shipments/shipments-page");
                  return { Component: ShipmentsPageWithActiveView };
                },
              },
            ],
          },
          {
            path: "tracking-page/login-form-builder",
            lazy: async () => {
              const { OrderLookupSettingsBuilderPage } =
                await import("@redotech/redo-merchant-marketing-app/order-tracking/tracking-page/order-lookup-settings-builder");
              return { Component: OrderLookupSettingsBuilderPage };
            },
          },
          {
            path: "tracking-page/tracking-page-builder",
            lazy: async () => {
              const { TrackingPageBuilderPage } =
                await import("@redotech/redo-merchant-marketing-app/order-tracking/tracking-page/tracking-page-builder");
              return { Component: TrackingPageBuilderPage };
            },
          },
          {
            path: "tracking-page",
            handle: { breadcrumb: "Tracking page" },
            lazy: async () => {
              const { TrackingPage } =
                await import("@redotech/redo-merchant-marketing-app/order-tracking/tracking-page/tracking-page");
              return { Component: TrackingPage };
            },
            children: [
              {
                index: true,
                lazy: async () => {
                  const { TrackingPagesTab } =
                    await import("@redotech/redo-merchant-marketing-app/order-tracking/tracking-page/tabs/tracking-pages-tab");
                  return { Component: TrackingPagesTab };
                },
              },
              {
                path: "flow",
                lazy: async () => {
                  const { EditTrackingPageFlowPage } =
                    await import("../../../setting/tracking-page-flow");
                  return { Component: EditTrackingPageFlowPage };
                },
              },
            ],
          },
        ],
      },
      {
        path: "contextual",
        handle: { breadcrumb: "Contextual" },
        element: <ContextualFeatureGate />,
        children: [
          {
            index: true,
            lazy: async () => {
              const { AiContextualPage } =
                await import("@redotech/redo-merchant-marketing-app/contextual-ai/ai-contextual-page");
              return { Component: AiContextualPage };
            },
          },
          {
            path: "dashboard",
            handle: { breadcrumb: "Dashboard" },
            lazy: async () => {
              const { ContextualLandingPage } =
                await import("@redotech/redo-merchant-marketing-app/contextual/contextual-landing-page");
              return { Component: ContextualLandingPage };
            },
          },
        ],
      },
      {
        path: "text-concierge",
        handle: { breadcrumb: "Text Concierge" },
        children: [
          { index: true, element: <TextConciergeRedirect /> },
          {
            path: "setup",
            handle: { breadcrumb: "Setup" },
            element: <TextConciergeRedirect />,
          },
          {
            path: "settings",
            handle: { breadcrumb: "Settings" },
            element: <TextConciergeRedirect subPath="settings" />,
          },
          {
            path: "conversations",
            handle: { breadcrumb: "Conversations" },
            element: <TextConciergeRedirect subPath="conversations" />,
          },
          {
            path: "analytics",
            handle: { breadcrumb: "Analytics" },
            element: <TextConciergeRedirect subPath="analytics" />,
          },
        ],
      },
      {
        path: "discounts",
        element: <DiscountUiFeatureGate />,
        handle: {
          breadcrumb: "Discounts",
          permission: [
            GeneralPermissions.OTHER,
            MarketingPermissions.MANAGE_CAMPAIGNS,
          ],
        },
        children: [
          {
            index: true,
            async lazy() {
              const { DiscountsPage } =
                await import("@redotech/redo-merchant-marketing-app/discounts/discounts-page");
              return { Component: DiscountsPage };
            },
          },
          {
            path: ":discountId",
            handle: { breadcrumb: "Discount" },
            async lazy() {
              const { DiscountDetailPage } =
                await import("@redotech/redo-merchant-marketing-app/discounts/discount-detail-page");
              return { Component: DiscountDetailPage };
            },
          },
        ],
      },
      { path: "email-sms", children: emailSmsRoutes },
      { path: "automations", children: automationRoutes },
      { path: "spotlights", children: spotlightRoutes },
      {
        path: "email-builder/click-map/:templateId",
        handle: { breadcrumb: "Click Map" },
        async lazy() {
          const { FullscreenClickMap } =
            await import("@redotech/redo-merchant-marketing-app/email-builder/click-map/fullscreen-click-map");
          return { Component: FullscreenClickMap };
        },
      },
      ...backwardsCompatibleEmailSmsRoutes,
    ],
  },
];

export const backwardsCompatibleMarketingRoutes: PermissionedRouteObject[] = [
  {
    path: "reviews",
    children: [
      {
        children: [
          {
            index: true,
            element: (
              <Redirect match={/reviews/} replaceValue="marketing/reviews" />
            ),
          },
          {
            path: "*",
            element: (
              <Redirect match={/reviews/} replaceValue="marketing/reviews" />
            ),
          },
        ],
      },
    ],
  },
  {
    path: "tracking",
    children: [
      {
        children: [
          {
            index: true,
            element: (
              <Redirect match={/tracking/} replaceValue="marketing/tracking" />
            ),
          },
          {
            path: "*",
            element: (
              <Redirect match={/tracking/} replaceValue="marketing/tracking" />
            ),
          },
        ],
      },
    ],
  },
];
